import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { ListChecks, Inbox } from 'lucide-react'
import { apiGet } from '../../lib/api.js'

/**
 * Second-level tabs inside พนักงาน › การลา.
 *
 * The two are genuinely different objects and were collapsing into each other:
 * ยอดวันลา is the LEDGER (employee_timetable rows of type ลากิจ/ลาป่วย, measured
 * against the annual quota), while คำขอลา is the QUEUE of things nobody has decided
 * yet. Nesting them here rather than adding a top-level tab keeps the request in
 * arm's reach of the ledger it will become — approving a request writes exactly the
 * rows the other tab counts.
 *
 * Pills, not a second underline row: two identical tab rows stacked read as one
 * confused row. Same treatment as ตารางเวร › ทันตแพทย์.
 *
 * Gate and proxy already agree — this section is RequireStaff (Manager/GM/HR/Admin)
 * and both proxies are /api/hr/*, the same set — so no extra tab gate is needed.
 */
const TABS = [
  { to: '', end: true, label: 'ยอดวันลา', icon: ListChecks, hint: 'โควตาและวันลาที่บันทึกแล้ว' },
  { to: 'requests', label: 'คำขอลา', icon: Inbox, hint: 'คำขอลาที่รอพิจารณา', badge: true },
]

export default function LeaveLayout() {
  const [pending, setPending] = useState(0)

  // Work waiting on a human. A request filed on Friday sits over the weekend, and
  // without a number on the tab nobody opens it until the employee asks in person —
  // which is the situation this whole feature exists to end.
  useEffect(() => {
    let alive = true
    apiGet('/api/hr/leave-request', { status: 'pending' })
      .then((r) => { if (alive) setPending(Number(r?.pending_count || 0)) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  return (
    <div>
      <nav className="flex items-center gap-1 mb-5">
        {TABS.map(({ to, end, label, icon: Icon, hint, badge }) => (
          <NavLink
            key={to || 'index'}
            to={to ? `/manager/employee/leave/${to}` : '/manager/employee/leave'}
            end={end}
            title={hint}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
                isActive
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 font-medium'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
              }`
            }
          >
            <Icon size={13} strokeWidth={1.75} />
            <span>{label}</span>
            {badge && pending > 0 && (
              <span className="ml-0.5 rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200 px-1.5 text-[11px] font-semibold leading-[17px]">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
